import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { useBilling } from '../../../contexts/BillingContext';
import { BillingConfig, TaxClass, TaxRate } from '../../../types/billing';
import { calculateLine, formatMoney, makeId, taxFields, todayLusaka } from '../../../utils/billingMoney';
import { Button } from '../../../components/ui/Button';
import { SettingsPanel, Toggle } from './SettingsPanel';
const classes: TaxClass[] = ['Standard', 'Zero-rated', 'Exempt'];

/** Tax codes, rates, classifications and defaults. Changes affect new transactions only. */
export function TaxSettings() {
  const {
    config,
    saveTaxes,
    can
  } = useBilling();
  const [draft, setDraft] = useState<BillingConfig['taxes']>(structuredClone(config.taxes));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  useEffect(() => {
    if (!dirty) setDraft(structuredClone(config.taxes));
  }, [config.taxes, dirty]);
  const change = (patch: Partial<BillingConfig['taxes']>) => {
    setDraft(d => ({
      ...d,
      ...patch
    }));
    setDirty(true);
    setStatus(null);
  };
  const setRate = (id: string, patch: Partial<TaxRate>) => change({
    rates: draft.rates.map(r => r.id === id ? {
      ...r,
      ...patch,
      rate: patch.classification && patch.classification !== 'Standard' ? 0 : patch.rate ?? r.rate
    } : r)
  });
  const save = async () => {
    setSaving(true);
    const result = await saveTaxes(draft);
    setSaving(false);
    if (!result.ok) return setStatus({
      ok: false,
      text: `${result.error ?? 'Not saved.'} Your changes are still here.`
    });
    setDirty(false);
    setStatus({
      ok: true,
      text: result.message ?? 'Saved.'
    });
  };
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const def = draft.rates.find(r => r.id === draft.defaultTaxId) ?? draft.rates[0];
  const sample = calculateLine({
    id: 's',
    description: '',
    quantity: 1,
    unitPrice: 100,
    discountPct: 0,
    ...taxFields(def),
    taxInclusive: draft.pricesIncludeTax
  });
  return <SettingsPanel title="Tax" description="Configure only rates you have verified for BMED's registration — medical services are not assumed to be taxable. Each issued line stores its own tax code, rate, taxable amount and tax, so changes here never recalculate history." dirty={dirty} saving={saving} status={status} canEdit={can('manageTaxes')} onSave={save} onReset={() => {
    setDraft(structuredClone(config.taxes));
    setDirty(false);
    setStatus(null);
  }} data-id="element-5592" data-mp="s00p1yrvakrj">
      <div className="overflow-x-auto" data-id="element-5593" data-mp="47ag7yf9ghlh">
        <table className="min-w-full text-sm" data-id="element-5594" data-mp="4ag5bdnw3jh7">
          <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-5595" data-mp="5ynhk13bg92"><tr data-id="element-5596" data-mp="3rdkunmn0q0uo"><th className="py-2 pr-2" data-id="element-5597" data-mp="3rdtanneftma3">Code</th><th className="py-2 pr-2" data-id="element-5598" data-mp="3remas5iw5dj9">Name</th><th className="py-2 pr-2" data-id="element-5599" data-mp="1ukrh5diexwyd">Classification</th><th className="py-2 pr-2" data-id="element-5600" data-mp="1ujq6ha5b7ch6">Rate %</th><th className="py-2 pr-2" data-id="element-5601" data-mp="1uiosoxwwsbvr">Effective from</th><th className="py-2 pr-2" data-id="element-5602" data-mp="1pl56nbtyyut9">Active</th><th className="py-2" data-id="element-5603" data-mp="1pm7s348iw51s">Default</th></tr></thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5604" data-mp="1x5phgy8g7p69">
            {draft.rates.map(r => <tr key={r.id} data-id="element-5605" data-mp="2240o703g5jk9">
                <td className="py-2 pr-2" data-id="element-5606" data-mp="222zfh29wl95p"><label className="sr-only" htmlFor={`tx-c-${r.id}`} data-id="element-5607" data-mp="222fypovzoqt9">Code</label><input id={`tx-c-${r.id}`} value={r.code ?? ''} maxLength={6} onChange={e => setRate(r.id, {
                code: e.target.value.toUpperCase()
              })} className="w-16 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-5608" data-mp="26w1zaiovp48p" /></td>
                <td className="py-2 pr-2" data-id="element-5609" data-mp="1e864jjivdmek"><label className="sr-only" htmlFor={`tx-n-${r.id}`} data-id="element-5610" data-mp="1e8oaj7vc3ezo">Name</label><input id={`tx-n-${r.id}`} value={r.name} onChange={e => setRate(r.id, {
                name: e.target.value
              })} className="w-full min-w-[160px] rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-5611" data-mp="1j5y7r9oysuy2" /></td>
                <td className="py-2 pr-2" data-id="element-5612" data-mp="1s7fkqj72sgij"><label className="sr-only" htmlFor={`tx-k-${r.id}`} data-id="element-5613" data-mp="1s3rtjqp3s66z">Classification</label><select id={`tx-k-${r.id}`} value={r.classification ?? 'Exempt'} disabled={r.id === 'exempt'} onChange={e => setRate(r.id, {
                classification: e.target.value as TaxClass
              })} className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm" data-id="element-5614" data-mp="2zjemmv2v5g32">{classes.map(c => <option key={c} data-id="element-5615" data-mp="2x62re439i5ot">{c}</option>)}</select></td>
                <td className="py-2 pr-2" data-id="element-5616" data-mp="3c2aat26uj331"><label className="sr-only" htmlFor={`tx-r-${r.id}`} data-id="element-5617" data-mp="3c2k0sprw5a8q">Rate</label><input id={`tx-r-${r.id}`} type="number" min={0} max={100} step={0.01} value={r.rate} disabled={r.classification !== 'Standard'} onChange={e => setRate(r.id, {
                rate: Number(e.target.value)
              })} className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-5618" data-mp="3bzxj3xbadecs" /></td>
                <td className="py-2 pr-2" data-id="element-5619" data-mp="3g1sslp3dey1n"><label className="sr-only" htmlFor={`tx-e-${r.id}`} data-id="element-5620" data-mp="3usi833nv6zbb">Effective from</label><input id={`tx-e-${r.id}`} type="date" value={r.effectiveFrom ?? ''} onChange={e => setRate(r.id, {
                effectiveFrom: e.target.value
              })} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-5621" data-mp="3uuukz2ermjhi" /></td>
                <td className="py-2 pr-2" data-id="element-5622" data-mp="3mqj47ltpsq3"><input type="checkbox" aria-label={`${r.name} active`} checked={r.active} disabled={r.id === 'exempt'} onChange={e => setRate(r.id, {
                active: e.target.checked
              })} className="rounded border-slate-300" data-id="element-5623" data-mp="3n9tn3959eu3" /></td>
                <td className="py-2" data-id="element-5624" data-mp="r9c11tbvg8j3"><input type="radio" name="tx-default" aria-label={`${r.name} is the default`} checked={draft.defaultTaxId === r.id} onChange={() => change({
                defaultTaxId: r.id
              })} data-id="element-5625" data-mp="r7rf2s3mhxwu" /></td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" onClick={() => change({
      rates: [...draft.rates, {
        id: makeId('tax'),
        code: '',
        name: '',
        rate: 0,
        classification: 'Standard',
        effectiveFrom: todayLusaka(),
        active: false
      }]
    })} data-id="element-5626" data-mp="3j85m98u7f36"><PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" data-id="element-5627" data-mp="3qqk24lw12l3l" />Add tax code</Button>
      <p className="text-xs text-slate-500" data-id="element-5628" data-mp="3urm1d2py2x5k">Codes cannot be deleted once created — deactivate them instead, so historical reports keep their labels. A code is only offered from its effective date. To change a rate, add a new code effective from the change date and deactivate the old one when it ends.</p>

      <div className="grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2" data-id="element-5629" data-mp="1rah668wdekre">
        <div className="space-y-3 pt-1 sm:col-span-2" data-id="element-5630" data-mp="1mipajjycxoue">
          <Toggle id="tx-incl" label="Catalogue prices include tax by default" hint="Items can override this individually." checked={draft.pricesIncludeTax} onChange={v => change({
          pricesIncludeTax: v
        })} data-id="element-5631" data-mp="1mh4ic0zimhzp" />
        </div>
        <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 sm:col-span-2" data-id="element-5632" data-mp="2a89vg8e2mw6b">
          <p className="font-semibold text-slate-800" data-id="element-5633" data-mp="266ye0q5cph97">How tax is calculated</p>
          <p className="mt-1" data-id="element-5634" data-mp="264tzzfdqn1v7">Per line, in this order: quantity × unit price → line discount → tax on the discounted amount ({draft.pricesIncludeTax ? 'extracted from an inclusive price' : 'added to an exclusive price'}) → rounded half-up to the nearest ngwee. Document totals are the sum of rounded lines.</p>
          <p className="mt-1" data-id="element-5635" data-mp="pqc2n4ghuryg">Example with the default code ({def?.code} {def?.rate}%): a {fmt(100)} line → net {fmt(sample.total - sample.tax)}, tax {fmt(sample.tax)}, total {fmt(sample.total)}.</p>
          <p className="mt-1" data-id="element-5636" data-mp="anu5nvp5fllt">The monthly tax report groups sales by tax code and rate, on the accrual basis. Tax registration (TPIN) is set under Business & branding; showing the tax breakdown on documents is under Printing & documents.</p>
        </div>
      </div>
    </SettingsPanel>;
}